import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subscription, forkJoin } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { Popover } from 'primeng/popover';
import { ConfigService } from '../../_services/config.service';
import { PortalService } from '../../portale/_services/portal.service';
import { MenuLayoutService } from '../../_services/menu-layout.service';
import { isMenuUrlActive, menuRouterUrl } from '../app-sidebar-nav/app-sidebar-nav.component';
import { collapseMenuHubs } from '../../_services/menu-hub';
import { conPadriInTendina, isMenuCategory, senzaVociNascoste } from '../../_services/menu-organization';
import { MenuEditService } from '../../_services/menu-edit.service';

/** Voce attiva sulla sua url o, per le sezioni a schede, su una delle pagine delle schede. */
function isItemUrlActive(router: Router, it: any): boolean {
	// pagina di un'altra sezione a schede sotto il suo url: si evidenzia quella sezione
	if ((it?.escludiUrls || []).some((u: string) => isMenuUrlActive(router, u))) return false;
	return isMenuUrlActive(router, it?.url) || (it?.hubUrls || []).some((u: string) => isMenuUrlActive(router, u));
}

/**
 * Menu orizzontale del portale (posizione menu = 'top', tab Impostazioni anagrafica).
 * Stesse voci della sidebar (getMenuItems, cacheato per-load in PortalService);
 * i sottomenu si aprono in popover agganciati al body, come la sidebar minimizzata.
 * Il menu sta SEMPRE su una sola riga: le voci che non entrano nella larghezza
 * disponibile confluiscono in un dropdown "Altro" (pattern "priority+").
 * Le larghezze si misurano su una lista fantasma nascosta (#measureList) che
 * contiene tutte le voci a larghezza naturale; il ricalcolo scatta al resize.
 * Visibile solo desktop: su mobile resta il drawer della sidebar (hamburger).
 */
@Component({
	standalone: false,
	selector: 'app-topnav',
	changeDetection: ChangeDetectionStrategy.Eager,
	template: ` @if (menuEdit.attivo()) {
		<!-- matita dell'header: il menu si modifica qui, al suo posto -->
		<app-menu-editor></app-menu-editor>
	} @else if (visible && navLoaded) {
		<nav #navRef class="app-topnav">
			<ul #realList class="topnav-list" [style.flex-wrap]="aCapo ? 'wrap' : null" [style.row-gap.px]="aCapo ? 2 : null" [style.align-content]="aCapo ? 'center' : null">
				@for (navitem of visibleItems; track navitem) {
					<li class="topnav-item">
						<app-topnav-item [item]="navitem" [ribbon]="stileBarra" [selezionata]="isScheda(navitem)" (scegli)="scegliScheda($event)"></app-topnav-item>
					</li>
				}
				@if (overflowItems.length) {
					<li class="topnav-item topnav-more-item">
						<app-topnav-item [item]="moreItem" [ribbonFigli]="stileBarra" (scegli)="scegliScheda($event)"></app-topnav-item>
					</li>
				}
			</ul>
			<!-- Lista fantasma per la misura delle larghezze (fuori flusso, nascosta) -->
			<ul #measureList class="topnav-list topnav-measure" aria-hidden="true">
				@for (navitem of navItems; track navitem) {
					<li class="topnav-item">
						<span class="topnav-link">
							@if (navitem.styleclass) {
								<i class="{{ navitem.styleclass }}"></i>
							}
							<span>{{ navitem.pg_title | translate }}</span>
							@if (navitem.submenu && navitem.submenu.length > 0) {
								<i class="pi pi-angle-down topnav-caret"></i>
							}
						</span>
					</li>
				}
			</ul>
		</nav>
		<!-- stile "barra come Word": il gruppo scelto apre sotto l'header una seconda barra che resta aperta -->
		@if (stileBarra && scheda && !schedaChiusa) {
			<app-menu-ribbon [gruppo]="scheda" [aCapo]="barraACapo" [blocchi]="barraBlocchi" (chiudi)="schedaChiusa = true"></app-menu-ribbon>
		}
	}`,
})
export class AppTopnavComponent implements OnInit, AfterViewChecked, OnDestroy {
	@ViewChild('navRef') navRef?: ElementRef<HTMLElement>;
	@ViewChild('realList') realList?: ElementRef<HTMLElement>;
	@ViewChild('measureList') measureList?: ElementRef<HTMLElement>;

	visible = false;
	navLoaded = false;
	/** Tutte le voci navigabili (senza divider/titoli). */
	navItems: any[] = [];
	/** Voci che entrano nella riga. */
	visibleItems: any[] = [];
	/** Voci che non entrano, confluite nel dropdown "Altro". */
	overflowItems: any[] = [];
	/** Voce sintetica "Altro" con i figli in overflow (pg_title tradotto dalla pipe). */
	moreItem: any = { pg_title: 'Altro', styleclass: 'pi pi-ellipsis-h', url: null, submenu: [] };

	/** Stile dei gruppi del menu in alto (cmp_menu_org.stileSottomenu): seconda barra come Word invece della tendina */
	stileBarra = false;
	/** Voci che non entrano: a capo (l'header si alza, cmp_menu_org.menuACapo) invece del dropdown "Altro" */
	aCapo = false;
	/** Seconda barra: voci a capo invece dello scorrimento orizzontale (cmp_menu_org.barraACapo) */
	barraACapo = false;
	/** Seconda barra: sottogruppi a blocchi col nome sotto invece della tendina in hover (cmp_menu_org.barraSottogruppi) */
	barraBlocchi = false;
	/** Gruppo mostrato nella seconda barra e se l'utente l'ha chiusa */
	scheda: any = null;
	schedaChiusa = false;

	private ro?: ResizeObserver;
	private rafId: number | null = null;
	private layoutSub?: Subscription;
	private menuChangedSub?: Subscription;
	private routerSub?: Subscription;

	constructor(
		private portalservice: PortalService,
		private configService: ConfigService,
		private menuLayoutService: MenuLayoutService,
		private zone: NgZone,
		public menuEdit: MenuEditService,
		private router: Router,
	) {}

	ngOnInit() {
		// come Word: la seconda barra segue il gruppo della pagina in cui si e'
		this.routerSub = this.router.events.subscribe((e) => {
			if (e instanceof NavigationEnd) this.aggiornaScheda();
		});
		this.layoutSub = this.menuLayoutService.layout$.subscribe((layout) => {
			this.visible = layout === 'top';
			if (!this.visible) this.altezzaHeader(0);
			if (this.visible && !this.navLoaded) {
				this.loadMenu();
			}
		});
		// l'organizzazione menu dell'azienda attiva e' cambiata: ricarica le voci
		this.menuChangedSub = this.portalservice.menuChanged$.subscribe(() => {
			if (this.navLoaded) {
				this.loadMenu();
			}
		});
	}

	ngAfterViewChecked() {
		// Aggancia il ResizeObserver una volta che la nav esiste nel DOM.
		// Osserviamo il contenitore (header-center) E il blocco icone/azioni: quando le
		// azioni si allargano (badge/pulsanti caricati async) lo spazio del menu cambia
		// e va ricalcolato. Lo spazio disponibile è misurato per posizioni (getBoundingClientRect),
		// non dalla larghezza della lista che stiamo mutando → niente feedback loop.
		if (this.navLoaded && this.navRef && !this.ro) {
			const nav = this.navRef.nativeElement;
			// <app-topnav> è un COMPONENTE: nav.parentElement è il suo host, non header-center.
			// Risaliamo con closest() al contenitore reale e al blocco azioni (suo fratello).
			const headerCenter = nav.closest('.header-center') as HTMLElement | null;
			const actions = headerCenter?.nextElementSibling as HTMLElement | null;
			this.zone.runOutsideAngular(() => {
				this.ro = new ResizeObserver(() => this.scheduleCompute());
				if (headerCenter) {
					this.ro.observe(headerCenter);
				}
				if (actions) {
					this.ro.observe(actions);
				}
			});
			this.scheduleCompute();
		}
	}

	ngOnDestroy() {
		this.layoutSub?.unsubscribe();
		this.menuChangedSub?.unsubscribe();
		this.routerSub?.unsubscribe();
		this.ro?.disconnect();
		this.altezzaHeader(0);
		if (this.rafId != null) {
			cancelAnimationFrame(this.rafId);
		}
	}

	/**
	 * Altezza dell'header con le voci a capo: oltre l'altezza normale (--kcs-header-height del tema)
	 * la si imposta sulla radice, cosi' header e corpo pagina (che la usano) scendono insieme. 0 = normale.
	 */
	private altezzaHeader(px: number) {
		const root = document.documentElement;
		root.style.removeProperty('--kcs-header-height');
		const normale = parseFloat(getComputedStyle(root).fontSize) * 4;
		if (px > normale + 4) root.style.setProperty('--kcs-header-height', Math.ceil(px) + 'px');
	}

	private chiave(n: any): string {
		return String(n?.pg_id ?? n?.pg_title ?? '');
	}

	private gruppo(n: any): boolean {
		return isMenuCategory(n) && !!n?.submenu?.length;
	}

	isScheda(n: any): boolean {
		return this.stileBarra && !this.schedaChiusa && !!this.scheda && this.chiave(n) === this.chiave(this.scheda);
	}

	/** Clic su un gruppo: apre la sua barra; sullo stesso gruppo gia' aperto la chiude. */
	scegliScheda(n: any) {
		if (this.isScheda(n)) {
			this.schedaChiusa = true;
			return;
		}
		this.scheda = n;
		this.schedaChiusa = false;
	}

	/** Gruppo che contiene la pagina corrente: la barra si apre li' (se l'utente non l'ha chiusa su quel gruppo); fuori dai gruppi si chiude. */
	private aggiornaScheda() {
		if (!this.stileBarra) {
			this.scheda = null;
			return;
		}
		const contiene = (it: any): boolean => isMenuUrlActive(this.router, it?.url) || (it?.hubUrls || []).some((u: string) => isMenuUrlActive(this.router, u)) || (it?.submenu || []).some(contiene);
		const g = this.navItems.find((n) => this.gruppo(n) && contiene(n));
		if (!g) {
			// pagina fuori dai gruppi (es. Dashboard): la seconda barra si chiude
			this.scheda = null;
			return;
		}
		if (!this.scheda || this.chiave(g) !== this.chiave(this.scheda)) {
			this.scheda = g;
			this.schedaChiusa = false;
		}
	}

	private scheduleCompute() {
		if (this.rafId != null) {
			return;
		}
		this.rafId = requestAnimationFrame(() => {
			this.rafId = null;
			this.zone.run(() => this.computeOverflow());
		});
	}

	private loadMenu() {
		const currentUser = this.configService.getLocalStorageObjectJson('currentUser');
		if (currentUser == null) {
			return;
		}
		const prt = this.configService.getPortalID();
		const cmp = this.configService.getCompany_id();
		forkJoin([this.portalservice.getMenuItems(prt, cmp), this.portalservice.getMenuHubs(prt, cmp), this.portalservice.getMenuStile(prt, cmp), this.portalservice.getMenuACapo(prt, cmp), this.portalservice.getMenuBarraACapo(prt, cmp), this.portalservice.getMenuBarraBlocchi(prt, cmp)]).subscribe(([menuItems, hubs, stile, aCapo, barraACapo, barraBlocchi]) => {
			// sezioni a schede: la voce padre apre il cruscotto, senza sottomenu
			// una voce con pagina e sottomenu apre la tendina: la sua pagina e' la prima voce
			this.navItems = conPadriInTendina(senzaVociNascoste(collapseMenuHubs(menuItems || [], hubs, cmp))).filter((m: any) => !m.divider && !m.title);
			this.visibleItems = this.navItems;
			this.overflowItems = [];
			this.stileBarra = stile === 'barra';
			this.aCapo = aCapo;
			this.barraACapo = barraACapo;
			this.barraBlocchi = barraBlocchi;
			if (!aCapo) this.altezzaHeader(0);
			// dopo una ricarica il gruppo aperto e' un oggetto nuovo: si ritrova per id
			const aperta = this.scheda ? this.navItems.find((n) => this.chiave(n) === this.chiave(this.scheda)) : null;
			this.scheda = aperta || null;
			this.aggiornaScheda();
			this.navLoaded = true;
			// su ricarica (menuChanged$) il ResizeObserver e' gia' agganciato: ricalcola qui
			this.scheduleCompute();
		});
	}

	/**
	 * Spazio orizzontale reale per il menu = distanza tra l'inizio della nav e l'inizio
	 * del blocco icone/azioni (header-center.nextElementSibling), misurata per posizioni.
	 * Robusto anche se il box del contenitore fosse dimensionato male dal flex.
	 */
	private availableWidth(nav: HTMLElement): number {
		const headerCenter = nav.closest('.header-center') as HTMLElement | null;
		const actions = headerCenter?.nextElementSibling as HTMLElement | null;
		const navLeft = nav.getBoundingClientRect().left;
		if (actions) {
			const w = actions.getBoundingClientRect().left - navLeft - 8; // 8px di margine
			if (w > 0) {
				return w;
			}
		}
		return headerCenter ? headerCenter.clientWidth : nav.clientWidth;
	}

	/**
	 * Divide navItems in visibili/overflow in base alla larghezza disponibile,
	 * misurando le voci sulla lista fantasma. Riserva spazio per il bottone "Altro".
	 */
	private computeOverflow() {
		const nav = this.navRef?.nativeElement;
		const measure = this.measureList?.nativeElement;
		if (!nav || !measure) {
			return;
		}
		if (this.aCapo) {
			// tutte le voci, su piu' righe: l'header si alza quanto serve (solo desktop, su mobile c'e' il drawer)
			if (this.visibleItems.length !== this.navItems.length || this.overflowItems.length) {
				this.visibleItems = this.navItems;
				this.overflowItems = [];
			}
			const lista = this.realList?.nativeElement;
			this.altezzaHeader(lista && window.innerWidth >= 768 ? lista.scrollHeight + 16 : 0);
			return;
		}
		const gap = 4; // .topnav-list gap: 0.25rem
		const available = this.availableWidth(nav);
		if (available < 80) {
			return; // larghezza non ancora affidabile (layout non ancora stabile)
		}
		const lis = Array.from(measure.children) as HTMLElement[];
		if (lis.length === 0) {
			return;
		}
		const widths = lis.map((li) => li.offsetWidth);
		const full = widths.reduce((a, b) => a + b, 0) + gap * (widths.length - 1);

		let visibleCount: number;
		if (full <= available) {
			visibleCount = this.navItems.length;
		} else {
			// serve il bottone "Altro": riserviamo la sua larghezza (misurata sul primo item + caret, stima prudente)
			const moreWidth = 96;
			let used = moreWidth;
			visibleCount = 0;
			for (let i = 0; i < widths.length; i++) {
				used += widths[i] + gap;
				if (used <= available) {
					visibleCount++;
				} else {
					break;
				}
			}
		}

		const nextVisible = this.navItems.slice(0, visibleCount);
		const nextOverflow = this.navItems.slice(visibleCount);
		// Riassegna solo se cambia qualcosa (evita render loop con ngAfterViewChecked)
		if (nextVisible.length !== this.visibleItems.length || nextOverflow.length !== this.overflowItems.length) {
			this.visibleItems = nextVisible;
			this.overflowItems = nextOverflow;
			this.moreItem = { ...this.moreItem, submenu: nextOverflow };
		}
	}
}

@Component({
	standalone: false,
	selector: 'app-topnav-item',
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@if (!hasSubMenu()) {
			<a class="topnav-link" [class.active]="isActive()" (click)="navigateTo(item.url)">
				@if (item.styleclass) {
					<i class="{{ item.styleclass }}"></i>
				}
				<span>{{ item.pg_title | translate }}</span>
			</a>
		} @else if (ribbon) {
			<!-- stile barra come Word: il clic apre/chiude la seconda barra, niente tendina in hover -->
			<!-- colorata se contiene la pagina corrente; aperta col clic ma senza esserci dentro = solo il contorno -->
			<a class="topnav-link" [class.active]="isActive()" [class.topnav-scheda-aperta]="selezionata" [attr.aria-expanded]="selezionata" (click)="scegli.emit(item)">
				@if (item.styleclass) {
					<i class="{{ item.styleclass }}"></i>
				}
				<span>{{ item.pg_title | translate }}</span>
				<i class="pi topnav-caret" [class.pi-angle-down]="!selezionata" [class.pi-angle-up]="selezionata"></i>
			</a>
		} @else {
			<a class="topnav-link" [class.active]="isActive()" (mouseenter)="onMouseEnterTrigger($event)" (mouseleave)="onMouseLeaveTrigger()" (click)="onTriggerClick($event)">
				@if (item.styleclass) {
					<i class="{{ item.styleclass }}"></i>
				}
				<span>{{ item.pg_title | translate }}</span>
				<i class="pi pi-angle-down topnav-caret"></i>
			</a>
			<p-popover #op appendTo="body" (onShow)="adjustPosition()" styleClass="menu-popover topnav-popover">
				<div (click)="op.hide()">
					<ul class="nav">
						@for (child of item.submenu; track child) {
							<li class="nav-item w-full" [class.topnav-has-sub]="child.submenu?.length" [class.sub-open]="isSubOpen(child)" (mouseenter)="onSubEnter($event)">
								<a class="pl-1 nav-link nav-dropdown-toggle" [class.active]="isChildActive(child)" (click)="onChildClick(child, $event)">
									@if (child.styleclass) {
										<i class="{{ child.styleclass }}"></i>
									}
									<span>{{ child.pg_title | translate }}</span>
									@if (child.submenu && child.submenu.length > 0) {
										<i class="pi pi-angle-down topnav-sub-caret" aria-hidden="true"></i>
									}
								</a>
								@if (child.submenu && child.submenu.length > 0) {
									<ul class="nav topnav-sub-nested">
										@for (nipote of child.submenu; track nipote) {
											<li class="nav-item w-full" [class.topnav-has-sub]="nipote.submenu?.length" [class.sub-open]="isSubOpen(nipote)" (mouseenter)="onSubEnter($event)">
												<a class="pl-1 nav-link nav-dropdown-toggle" [class.active]="isChildActive(nipote)" (click)="onChildClick(nipote, $event)">
													@if (nipote.styleclass) {
														<i class="{{ nipote.styleclass }}"></i>
													}
													<span>{{ nipote.pg_title | translate }}</span>
													@if (nipote.submenu && nipote.submenu.length > 0) {
														<i class="pi pi-angle-down topnav-sub-caret" aria-hidden="true"></i>
													}
												</a>
												<!-- 4° livello: raggiunto quando una categoria con sottocategorie finisce
												     nel dropdown "Altro" (che aggiunge un livello di annidamento). -->
												@if (nipote.submenu && nipote.submenu.length > 0) {
													<ul class="nav topnav-sub-nested">
														@for (pronipote of nipote.submenu; track pronipote) {
															<li class="nav-item w-full">
																<a class="pl-1 nav-link nav-dropdown-toggle" [class.active]="isChildActive(pronipote)" (click)="navigateTo(pronipote.url)">
																	@if (pronipote.styleclass) {
																		<i class="{{ pronipote.styleclass }}"></i>
																	}
																	<span>{{ pronipote.pg_title | translate }}</span>
																</a>
															</li>
														}
													</ul>
												}
											</li>
										}
									</ul>
								}
							</li>
						}
					</ul>
				</div>
			</p-popover>
		}
	`,
})
export class AppTopnavItemComponent {
	@Input() item: any;
	/** Stile barra come Word: il gruppo non apre la tendina, chiede al menu di aprire la seconda barra */
	@Input() ribbon = false;
	@Input() selezionata = false;
	/** "Altro" con lo stile barra: un gruppo scelto dentro la tendina apre la seconda barra */
	@Input() ribbonFigli = false;
	@Output() scegli = new EventEmitter<any>();
	@ViewChild('op') op!: Popover;
	private hideTimeout: any;

	constructor(private router: Router) {}

	hasSubMenu() {
		return this.item?.submenu && this.item.submenu.length > 0;
	}

	isActive(): boolean {
		// ricorsivo su tutti i livelli: le scorciatoie possono stare in sottocategorie
		const anyActive = (it: any): boolean => isItemUrlActive(this.router, it) || (it?.submenu || []).some(anyActive);
		return anyActive(this.item);
	}

	/**
	 * Attiva la voce col match più specifico dell'intero sottomenu (con URL annidati,
	 * es. hub "/companies" + scorciatoia "companies/locations/5", il match per prefisso
	 * accenderebbe entrambe) e le voci-genitore lungo il percorso della voce attiva
	 * (pagina con sottomenu: dentro "Dipendenti" si accendono "Aziende" e "Dipendenti").
	 */
	isChildActive(child: any): boolean {
		if ((child?.escludiUrls || []).some((u: string) => isMenuUrlActive(this.router, u))) {
			return false;
		}
		if (isMenuUrlActive(this.router, child?.url) && this.urlLen(child.url) >= this.bestMatchLen()) {
			return true;
		}
		if ((child?.hubUrls || []).some((u: string) => isMenuUrlActive(this.router, u))) {
			return true;
		}
		const anyDesc = (it: any): boolean => isMenuUrlActive(this.router, it?.url) || (it?.submenu || []).some(anyDesc);
		return (child?.submenu || []).some(anyDesc);
	}

	private urlLen(url: string): number {
		return ('/' + url.split('?')[0].split('#')[0].replace(/^\/+/, '').replace(/\/+$/, '')).length;
	}

	private bestMatchLen(): number {
		let best = 0;
		const visit = (items: any[]) => {
			for (const it of items || []) {
				if (isMenuUrlActive(this.router, it?.url)) {
					best = Math.max(best, this.urlLen(it.url));
				}
				if (it?.submenu?.length) {
					visit(it.submenu);
				}
			}
		};
		visit(this.item?.submenu || []);
		return best;
	}

	/** Sottocategorie aperte via click/tap (l'hover è gestito dal CSS, questo serve per il touch). */
	private openSubs = new Set<any>();

	isSubOpen(child: any): boolean {
		return this.openSubs.has(child);
	}

	onChildClick(child: any, event: Event) {
		if (this.ribbonFigli && isMenuCategory(child) && child?.submenu?.length) {
			this.scegli.emit(child); // il click risale al wrapper e chiude la tendina "Altro"
			return;
		}
		const url = child?.url;
		if (url != null && url !== '' && url !== 'null') {
			this.navigateTo(url); // il click risale al wrapper e chiude il popover
			return;
		}
		// sottocategoria: apre/chiude senza chiudere il popover
		event.stopPropagation();
		const li = (event.currentTarget as HTMLElement).closest('li');
		if (this.openSubs.has(child)) {
			this.openSubs.delete(child);
		} else {
			this.openSubs.add(child);
			// il flyout diventa visibile solo dopo il change detection (.sub-open)
			if (li) {
				requestAnimationFrame(() => this.flipIfNoSpace(li));
			}
		}
	}

	/** Hover su una voce con sottocategoria: decide da che lato aprire il flyout. */
	onSubEnter(event: Event) {
		this.flipIfNoSpace(event.currentTarget as HTMLElement);
	}

	/** Flyout a destra di default; se sfora il viewport e a sinistra c'è spazio, flippa. */
	private flipIfNoSpace(li: HTMLElement) {
		const sub = li.querySelector(':scope > .topnav-sub-nested') as HTMLElement | null;
		if (!sub) {
			return;
		}
		li.classList.remove('topnav-flip-left');
		const rect = sub.getBoundingClientRect();
		if (rect.width > 0 && rect.right > window.innerWidth - 8 && li.getBoundingClientRect().left - rect.width >= 8) {
			li.classList.add('topnav-flip-left');
		}
	}

	adjustPosition() {
		const trigger = this.op.target as HTMLElement;
		const popover = this.op.container as HTMLElement;
		const rect = trigger.getBoundingClientRect();
		popover.style.left = `${rect.left}px`;
		popover.style.top = `${rect.bottom + 6}px`;
		// L'hover va ascoltato sull'intero container (compreso il ponte ::before che copre
		// il gap trigger→popover), non solo sul contenuto: altrimenti attraversare il gap
		// diventa una corsa contro il timeout di chiusura. onmouseenter= è idempotente.
		popover.onmouseenter = () => this.onMouseEnterContent();
		popover.onmouseleave = () => this.onMouseLeaveContent();
	}

	onTriggerClick(event: any) {
		const url = this.item?.url;
		if (url != null && url != undefined && url != '' && url != 'null') {
			this.navigateTo(url);
		} else {
			// voce senza pagina propria: il click APRE il sottomenu (touch). Non usare
			// toggle(): su desktop l'hover l'ha già aperto, e toggle lo richiuderebbe
			// facendo lampeggiare il menu. show() è idempotente se già aperto; su touch
			// (senza hover) apre, e il tap fuori lo chiude come da comportamento popover.
			this.op.show(event);
		}
	}

	onMouseEnterTrigger($event: any) {
		clearTimeout(this.hideTimeout);
		this.op.show($event);
	}

	onMouseLeaveTrigger() {
		this.hideTimeout = setTimeout(() => this.op.hide(), 200);
	}

	onMouseEnterContent() {
		clearTimeout(this.hideTimeout);
	}

	onMouseLeaveContent() {
		// stessa grazia del trigger: uscire per un attimo (es. rientrando dal flyout)
		// non deve chiudere il menu all'istante
		clearTimeout(this.hideTimeout);
		this.hideTimeout = setTimeout(() => this.op.hide(), 200);
	}

	navigateTo(url: any) {
		if (url != null && url != undefined && url != '' && url != 'null') {
			/*KCS MI - pulizia variabili navigazione*/
			sessionStorage.clear();
			/*KCS MI*/
			// navigateByUrl: gestisce anche url con query string (es. scorciatoie hub 'hr/employees/5?mode_view=hr');
			// menuRouterUrl toglie gli slash finali presenti in alcune pagine del DB (NG04002)
			this.router.navigateByUrl(menuRouterUrl(url));
		}
	}
}

export const APP_TOPNAV = [AppTopnavComponent, AppTopnavItemComponent];
